/**
 * MATCH HISTORY STORE
 *
 * Shape (persisted under the key "matchHistory"):
 *   entries  MatchSummary[]   newest first, capped at HISTORY_LIMIT
 *
 * One MatchSummary per finished match:
 *   id            string   unique, generated at record time
 *   playedAt      number   epoch ms
 *   result        'win' | 'loss' | 'draw'   from the local player's side
 *   mode          'bot' | 'deckTest' | 'lab' | 'tutorial'
 *   playerDeckId  string
 *   opponentDeckId string
 *   opponentName  string
 *   difficulty    BotDifficulty | null
 *   turns         number
 *   damageDealt   number   integrity the local player took off the opponent
 *   damageTaken   number
 *   seed          number   with the deck ids, enough to replay the match
 *
 * The achievements milestone reads these, so fields are added rather than
 * renamed. `record` returns the stored summary.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { BotDifficulty } from '@/ai/types';
import type { Faction } from '@/engine';
import { adapterStorage } from '@/persist';

export type MatchResult = 'win' | 'loss' | 'draw';
export type MatchMode = 'bot' | 'deckTest' | 'lab' | 'tutorial';

export interface MatchSummary {
  id: string;
  playedAt: number;
  result: MatchResult;
  mode: MatchMode;
  playerDeckId: string;
  opponentDeckId: string;
  opponentName: string;
  difficulty: BotDifficulty | null;
  turns: number;
  damageDealt: number;
  damageTaken: number;
  seed: number;

  /*
   * Achievement fields, milestone 9. Every one is optional and every one is a
   * measurement the battle screen takes, so a summary recorded by an older
   * build, or by a caller that does not measure a given thing, is still valid.
   * The evaluator treats an absent field as "not measured" rather than as
   * false or zero, so a missing measurement can never unlock anything.
   */
  /** Integrity the local player finished on. */
  playerIntegrityEnd?: number;
  /** The lowest the local player's Integrity ever got. */
  minIntegrity?: number;
  /** Did the local player lose any Integrity at all this game? */
  lostIntegrity?: boolean;
  /** Did the winning blow deal exactly the Integrity the loser had left? */
  exactLethal?: boolean;
  /** Most Integrity taken off the opponent in one combat phase. */
  maxDamageOneCombat?: number;
  /** Most characters destroyed by a single card. */
  maxDestroyedByOneCard?: number;
  /** Factions present in the local player's deck, neutral excluded. */
  factionsUsed?: Faction[];
  /** How many of those there were. */
  deckFactionCount?: number;
  /** Most Legendary characters the local player controlled at one time. */
  legendariesControlledAtOnce?: number;
  /** Earliest turn a card costing 6 or more was deployed. Null if never. */
  earliestExpensiveDeploy?: number | null;
  /** Was the local player's deck empty when they won? */
  deckEmptyAtWin?: boolean;
  /** Did the local player take their Redraw? */
  redrew?: boolean;
  /** Did the local player control a Drainer Link character at the win? */
  drainerLinkWin?: boolean;
  /** Did a Frontrun attacker kill its blocker in the Frontrun damage step? */
  frontrunKill?: boolean;
  /** Did the local player block four or more attackers and survive the combat? */
  blocked4Survived?: boolean;
  /** Highest Attack the local player ever had on one of their characters. */
  maxAttackControlled?: number;
}

export const HISTORY_LIMIT = 100;

export interface MatchHistoryState {
  entries: MatchSummary[];
}

export interface MatchHistoryActions {
  record(summary: Omit<MatchSummary, 'id' | 'playedAt'>): MatchSummary;
  recent(count?: number): MatchSummary[];
  clear(): void;
}

export type MatchHistoryStore = MatchHistoryState & MatchHistoryActions;

export const useMatchHistory = create<MatchHistoryStore>()(
  persist(
    (set, get) => ({
      entries: [],
      record(summary) {
        const entry: MatchSummary = {
          ...summary,
          id: `m-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
          playedAt: Date.now(),
        };
        set({ entries: [entry, ...get().entries].slice(0, HISTORY_LIMIT) });
        /*
         * Achievements are evaluated here as well as on the battle screen's
         * game-over path. Unlocking is idempotent (an already-unlocked id keeps
         * its original date), so the double call is harmless, and it means a
         * match recorded by any caller is always scored.
         */
        void import('@/achievements/hooks')
          .then((m) => m.recordMatchForAchievements(entry))
          .catch(() => {});
        return entry;
      },
      recent(count = 10) {
        return get().entries.slice(0, count);
      },
      clear() {
        set({ entries: [] });
      },
    }),
    {
      name: 'matchHistory',
      storage: adapterStorage<Pick<MatchHistoryStore, 'entries'>>(),
      partialize: (state) => ({ entries: state.entries }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<MatchHistoryState> | undefined),
      }),
    },
  ),
);
